import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Loader2, Send, Sparkles, Volume2, Pause } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { AppHeader } from "../components/elysium/AppHeader";
import { useTts } from "../components/elysium/useTts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SoulKeyChat = () => {
  const { id } = useParams();
  const { user, token } = useAuth();
  const [sk, setSk] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const scrollRef = useRef(null);
  const authHeader = useRef({ Authorization: `Bearer ${token}` }).current;
  const { playingKey, loadingKey, toggle } = useTts(`${API}/tts`, authHeader);

  const load = useCallback(async () => {
    try {
      const skRes = await axios.get(`${API}/soulkeys/${id}`);
      setSk(skRes.data);
      const h = await axios.get(`${API}/soulkeys/${id}/chat/history`);
      setMessages(h.data.messages);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, [id]);

  useEffect(() => { if (user) load(); }, [user, load]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages]);

  if (user === null) return <div className="flex min-h-screen items-center justify-center bg-ely-bg"><Loader2 className="animate-spin text-ely-primary" /></div>;
  if (user === false) return <Navigate to="/login" replace />;

  const send = async (e) => {
    e.preventDefault();
    const msg = input.trim();
    if (!msg || sending) return;
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/soulkeys/${id}/chat`, { message: msg });
      setMessages((m) => [...m, { role: "assistant", content: data.reply, sources: data.sources }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: `⚠️ ${formatApiErrorDetail(e.response?.data?.detail) || e.message}`, error: true }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-ely-bg">
      <div className="grain" aria-hidden="true" />
      <AppHeader />
      <main className="mx-auto flex w-full max-w-3xl flex-1 flex-col px-6 py-10 md:px-10" data-testid="soulkey-chat">
        <Link to={`/soulkey/${id}`} className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-ely-muted transition-colors hover:text-ely-primary" data-testid="back-to-detail">
          <ArrowLeft size={13} /> Back
        </Link>

        {sk && (
          <>
            <h1 className="mt-5 font-serif text-3xl font-light italic tracking-tight text-ely-text md:text-5xl">Talking with {sk.name}</h1>
            <div className="mt-4 border-l-2 border-ely-primary/50 bg-ely-primary/[0.05] px-5 py-3 font-sans text-sm font-light text-ely-text/80">
              This is an AI reflection built from {sk.name}'s recorded stories — not {sk.name} themselves. Mode: <b className="text-ely-primary">{sk.my_mode}</b>.
            </div>
          </>
        )}

        <div ref={scrollRef} className="mt-6 flex-1 space-y-6 overflow-y-auto py-4" data-lenis-prevent>
          {messages.length === 0 && !err && (
            <p className="py-16 text-center font-serif text-xl font-light text-ely-muted">Ask about a memory, a story, or a piece of advice…</p>
          )}
          {err && messages.length === 0 && <p className="font-sans text-sm text-red-400">{err}</p>}

          {messages.map((m, idx) =>
            m.role === "user" ? (
              <div key={idx} className="flex justify-end">
                <div className="max-w-md border-r-2 border-ely-border pr-4 text-right">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ely-muted">You</span>
                  <p className="mt-1 font-sans text-base font-light text-ely-text/90">{m.content}</p>
                </div>
              </div>
            ) : (
              <div key={idx} className="border-l-2 border-ely-primary pl-4" data-testid={`assistant-msg-${idx}`}>
                <div className="mb-2 flex items-center gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ely-primary">{sk?.name}</span>
                  <span className="flex items-center gap-1 rounded-full border border-ely-border px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-ely-muted">
                    <Sparkles size={9} /> AI reflection
                  </span>
                </div>
                <p className="whitespace-pre-wrap font-sans text-base font-light leading-relaxed text-ely-text/90">{m.content}</p>
                {m.sources && m.sources.length > 0 && (
                  <p className="mt-3 font-mono text-[10px] uppercase tracking-wider text-ely-primary/80" data-testid={`sources-${idx}`}>
                    Based on: {m.sources.map((s) => s.title || "a recorded story").join(", ")}
                  </p>
                )}
                {!m.error && (
                  <button
                    onClick={() => toggle(`msg-${idx}`, m.content)}
                    data-testid={`listen-${idx}`}
                    className="mt-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-ely-muted transition-colors hover:text-ely-primary"
                  >
                    {loadingKey === `msg-${idx}` ? <Loader2 size={12} className="animate-spin" /> : playingKey === `msg-${idx}` ? <Pause size={12} /> : <Volume2 size={12} />}
                    {playingKey === `msg-${idx}` ? "Playing…" : "Listen aloud"}
                  </button>
                )}
              </div>
            )
          )}
          {sending && <div className="flex items-center gap-2 pl-4 text-ely-muted"><Loader2 size={13} className="animate-spin" /> <span className="font-mono text-[10px] uppercase tracking-wider">reflecting…</span></div>}
        </div>

        <form onSubmit={send} className="mt-4 flex items-center gap-3 border-t border-ely-border/50 pt-5" data-testid="chat-form">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about a memory, a story, a piece of advice…"
            disabled={sending}
            data-testid="chat-input"
            className="flex-1 bg-transparent font-sans text-base text-ely-text placeholder:text-ely-muted/60 outline-none disabled:opacity-60"
          />
          <button type="submit" disabled={sending || !input.trim()} data-testid="chat-send" className="flex h-10 w-10 items-center justify-center rounded-full bg-ely-primary text-ely-bg transition-all hover:bg-ely-primaryHover disabled:opacity-40">
            {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
          </button>
        </form>
      </main>
    </div>
  );
};
